import { useState, useEffect } from "react";
import { Box, Slider, Typography, Paper, Button } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import axios from "axios";
import ImageBrowserPopup from "../PreviousImagePopup/popup"; // Adjust the path as needed
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function LipstickApp() {
  const [image, setImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [lipstickAlpha, setLipstickAlpha] = useState(50);
  const [upperLipColor, setUpperLipColor] = useState({ r: 255, g: 0, b: 0 });
  const [scanning, setScanning] = useState(false);
  const [imageBrowserOpen, setImageBrowserOpen] = useState(false);

  // Common button style from BlushApp
  const commonButtonStyle = {
    textTransform: "none",
    borderRadius: "8px",
    padding: "12px 20px",
    minWidth: "160px",
    fontSize: "16px",
    width: "200px",
  };

  // Removed auto processing useEffect to allow processing only on demand.
  // On mount, check localStorage for a stored image URL and load it if available
  useEffect(() => {
    const storedImageUrl = localStorage.getItem("selectedImageUrl");
    if (storedImageUrl && !image) {
      handlePreviousImageSelect({ url: storedImageUrl });
    } // eslint-disable-next-line
  }, []);

  // Handle image selection from the popup and persist it in localStorage
  const handlePreviousImageSelect = async (selectedImage) => {
    setScanning(true);
    try {
      localStorage.setItem("selectedImageUrl", selectedImage.url);
      const response = await fetch(selectedImage.url);
      const blob = await response.blob();
      const file = new File([blob], "previous-image.jpg", {
        type: "image/jpeg",
      });
      setProcessedImage(URL.createObjectURL(file));
      setImage(file);
    } catch (error) {
      console.error("Error processing previous image:", error);
      toast.error("Error processing previous image.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
    setImageBrowserOpen(false);
    setScanning(false);
  };

  // Process the image using the lipstick API
  const processImage = async (file, currentUpperLipColor, currentAlpha) => {
    setScanning(true);
    try {
      // Step 1: Upload the file to get an intermediate image
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_KEY}/makeup/upload`,
        formDataUpload,
        {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        }
      );
      if (!uploadResponse.data) {
        throw new Error("Upload API did not return image data");
      }
      const intermediateImageUrl = URL.createObjectURL(uploadResponse.data);
      setProcessedImage(intermediateImageUrl);

      // Delay for the scanning animation (3 seconds)
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Step 2: Apply lipstick effect via API call
      const formDataLipstick = new FormData();
      formDataLipstick.append("file", file);
      formDataLipstick.append(
        "lip_color",
        `${currentUpperLipColor.b},${currentUpperLipColor.g},${currentUpperLipColor.r}`
      );
      formDataLipstick.append(
        "lipstick_alpha",
        (currentAlpha / 100).toString()
      );
      const url = `${process.env.REACT_APP_API_KEY}/makeup/apply_lipstick`;
      const response = await axios.post(url, formDataLipstick, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });
      if (!response.data) {
        throw new Error("Lipstick API did not return image data");
      }
      setProcessedImage(URL.createObjectURL(response.data));
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Error processing image. Please try another image.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
    setScanning(false);
  };

  // Handler for Apply Lipstick button; only processes when user clicks the button
  const handleApply = () => {
    if (image) {
      processImage(image, upperLipColor, lipstickAlpha);
    } else {
      toast.error("Please select or upload an image first.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 4,
        p: 4,
        pt: 16,
        pb: 16,
        minHeight: "100vh",
        width: "100%",
        bgcolor: "#0d1117",
        fontFamily: "Roboto, sans-serif",
        color: "#fff",
      }}
    >
      <ImageBrowserPopup
        open={imageBrowserOpen}
        onClose={() => setImageBrowserOpen(false)}
        onImageSelect={handlePreviousImageSelect}
      />

      {/* Processed Image Display */}
      <Paper
        elevation={6}
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 3,
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(12px)",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          height: { xs: "auto", md: "80vh" },
          width: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {processedImage ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={processedImage}
              alt="Processed"
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100% - 80px)", // Adjusted to match BlushApp
                borderRadius: "12px",
                transition: "0.3s ease",
                boxShadow: "0px 5px 20px rgba(255, 255, 255, 0.1)",
              }}
            />
            {scanning && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "110%",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                {/* Horizontal scanning line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    width: "100%",
                    height: "3px",
                    background:
                      "linear-gradient(90deg, transparent, #EDB38F, #F98995, #A41847, transparent)",
                    boxShadow:
                      "0 0 10px #EDB38F, 0 0 20px #F98995, 0 0 30px #A41847",
                    animation: "scanHorizontal 2s ease-in-out infinite",
                    "@keyframes scanHorizontal": {
                      "0%": { top: "10%", opacity: 0 },
                      "15%": { opacity: 1 },
                      "85%": { opacity: 1 },
                      "100%": { top: "90%", opacity: 0 },
                    },
                  }}
                />
                {/* Vertical scanning line */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    height: "100%",
                    width: "3px",
                    background:
                      "linear-gradient(0deg, transparent, #EDB38F, #F98995, #A41847, transparent)",
                    boxShadow:
                      "0 0 10px #EDB38F, 0 0 20px #F98995, 0 0 30px #A41847",
                    animation: "scanVertical 2s ease-in-out infinite",
                    animationDelay: "1s",
                    "@keyframes scanVertical": {
                      "0%": { left: "10%", opacity: 0 },
                      "15%": { opacity: 1 },
                      "85%": { opacity: 1 },
                      "100%": { left: "90%", opacity: 0 },
                    },
                  }}
                />
                {/* Pulsing overlay effect */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background:
                      "radial-gradient(circle, transparent, rgba(237, 179, 143, 0.05))",
                    animation: "pulse 1.5s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 0.1 },
                      "50%": { opacity: 0.3 },
                      "100%": { opacity: 0.1 },
                    },
                  }}
                />
              </Box>
            )}
            {/* Status / Download Button */}
            <Box sx={{ mt: 2, textAlign: "center" }}>
              {scanning ? (
                <Typography
                  sx={{
                    color: "#F98995",
                    fontSize: { xs: 16, md: 18 },
                    fontWeight: "bold",
                  }}
                >
                  Beautifying in progress...
                  <br />
                  Getting your glow-up started!
                </Typography>
              ) : (
                <a href={processedImage} download="lipstick_applied.png">
                  <Button
                    variant="contained"
                    sx={{
                      ...commonButtonStyle,
                      background: "#a41847",
                      "&:hover": { background: "#860f37" },
                    }}
                  >
                    Download Image
                  </Button>
                </a>
              )}
            </Box>
          </Box>
        ) : (
          <Typography sx={{ color: "#ccc", fontSize: 18, textAlign: "center" }}>
            {scanning
              ? "Scanning image..."
              : "Upload an image to apply lipstick"}
          </Typography>
        )}
      </Paper>

      {/* Controls */}
      <Paper
        elevation={6}
        sx={{
          p: 4,
          borderRadius: 3,
          background: "rgba(255,255,255,0.05)",
          backdropFilter: "blur(12px)",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
          height: { xs: "auto", md: "80vh" },
          display: "flex",
          flexDirection: "column",
          gap: 3,
          color: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "uploaded");
            }}
          >
            Uploaded Images
          </Button>
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "captured");
            }}
          >
            Captured Images
          </Button>
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "model");
            }}
          >
            Model Image
          </Button>
        </Box>

        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Lipstick Intensity
        </Typography>
        <Slider
          value={lipstickAlpha}
          min={0}
          max={80}
          onChange={(e, newValue) => setLipstickAlpha(Number(newValue))}
          sx={{ color: "#a41847", height: 6 }}
        />

        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Lip Color
        </Typography>
        <input
          type="color"
          value={`#${upperLipColor.r
            .toString(16)
            .padStart(2, "0")}${upperLipColor.g
            .toString(16)
            .padStart(2, "0")}${upperLipColor.b.toString(16).padStart(2, "0")}`}
          onChange={(e) => {
            const hex = e.target.value;
            setUpperLipColor({
              r: parseInt(hex.slice(1, 3), 16),
              g: parseInt(hex.slice(3, 5), 16),
              b: parseInt(hex.slice(5, 7), 16),
            });
          }}
          style={{
            width: "100%",
            height: 50,
            border: "none",
            cursor: "pointer",
            borderRadius: "6px",
            background: "none",
            boxShadow: "0px 3px 12px rgba(255,255,255,0.1)",
          }}
        />

        {/* Apply Lipstick Button */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={scanning}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
              width: { xs: "100%", md: "auto" },
              mx: "auto", // centers horizontally if width < 100%
              display: "block",
            }}
          >
            Apply Lipstick
          </Button>
        </Box>
      </Paper>

      <ToastContainer />
    </Box>
  );
}
